import React from 'react';
import { ChangelogModel } from './useChangelogData';

const ChangelogEntriesList: React.FC<{ model: ChangelogModel }> = ({ model }) => {
  const {
    loading, filteredEntries, sortedVersions, groupedByVersion, selectedEntries,
    toggleSelection, toggleVersionGroup, getCategoryInfo, openEditForm,
    setDeleteConfirm, openCreateForm,
  } = model;

  if (loading) {
    return (
      <div className="rp-bento-card text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 rounded-full mx-auto mb-4" style={{ borderColor: 'var(--rp-border)', borderTopColor: 'var(--rp-text-secondary)' }} />
        <p style={{ color: 'var(--rp-text-secondary)' }}>Loading changelog entries...</p>
      </div>
    );
  }

  if (filteredEntries.length === 0) {
    return (
      <div className="rp-bento-card text-center py-12">
        <p className="text-lg mb-2" style={{ color: 'var(--rp-text-secondary)' }}>No entries found</p>
        <button onClick={openCreateForm}
          className="px-4 py-2 rounded-lg font-semibold text-sm"
          style={{ backgroundColor: 'var(--rp-primary)', color: '#fff' }}
        >
          Create your first entry
        </button>
      </div>
    );
  }

  return (
    <>
      {sortedVersions.map(version => (
        <div key={version} className="mb-6">
          {/* Version header */}
          <div className="flex items-center gap-3 mb-3 px-1">
            <h3 className="text-lg font-bold" style={{ color: 'var(--rp-text)' }}>
              {version}
            </h3>
            <span className="text-xs px-2 py-0.5 rounded-full"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'var(--rp-text-secondary)' }}
            >
              {groupedByVersion[version].length} {groupedByVersion[version].length === 1 ? 'entry' : 'entries'}
            </span>
            <label className="ml-auto inline-flex items-center gap-2 cursor-pointer">
              <span className="sr-only">Select all in {version}</span>
              <input type="checkbox"
                checked={groupedByVersion[version].every(e => selectedEntries.has(e.id))}
                onChange={() => toggleVersionGroup(version)}
                className="rounded border-white/20 bg-white/5"
                title="Select all in this build"
              />
            </label>
          </div>

          {/* Entries for this version */}
          <div className="space-y-2">
            {groupedByVersion[version].map(entry => {
              const cat = getCategoryInfo(entry.category);
              return (
                <div key={entry.id}
                  className="rp-bento-card flex items-start gap-4 py-3 px-4"
                  style={{ opacity: entry.is_published ? 1 : 0.7 }}
                >
                  <label className="inline-flex items-center cursor-pointer py-1" aria-label={`Select ${entry.title}`}>
                    <input type="checkbox"
                      checked={selectedEntries.has(entry.id)}
                      onChange={() => toggleSelection(entry.id)}
                      className="rounded border-white/20 bg-white/5"
                    />
                  </label>
                  {/* Category badge */}
                  <span className="w-3 h-3 rounded-full flex-shrink-0 mt-1.5" title={cat.label} style={{ backgroundColor: cat.color }} aria-hidden="true" />
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" style={{ color: 'var(--rp-text)' }}>{entry.title}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${cat.color}20`, color: cat.color, border: `1px solid ${cat.color}40` }}>
                        {cat.label}
                      </span>
                      {entry.is_published ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-400/30">
                          Published
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-400/30">
                          Draft
                        </span>
                      )}
                    </div>
                    {entry.description && (
                      <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--rp-text-secondary)' }}>
                        {entry.description}
                      </p>
                    )}
                    <div className="flex gap-3 mt-1 text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>
                      <span>{new Date(entry.created_at).toLocaleDateString()}</span>
                      <span>{entry.author_email}</span>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex gap-1 flex-wrap min-w-0">
                    <button onClick={() => openEditForm(entry)}
                      className="px-3 py-1.5 text-sm rounded-lg hover:bg-white/10 transition-colors"
                      style={{ color: '#3b82f6' }}
                    >
                      Edit
                    </button>
                    <button onClick={() => setDeleteConfirm(entry.id)}
                      className="px-3 py-1.5 text-sm rounded-lg hover:bg-white/10 transition-colors"
                      style={{ color: 'var(--admin-error)' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
};

export default ChangelogEntriesList;
